<script lang="ts" setup>
import { ref } from 'vue';
import { AuraChatMock, auraChatMocks } from '../mocks/auraChat';
import { SparklesIcon, UserIcon } from '@heroicons/vue/solid';
import moment from 'moment';
import AuraConversationIcon from '@/assets/icons/aura-conversation.svg';
import { RefreshIcon } from '@heroicons/vue/outline';

const auraPanelOpened = ref(false);

interface AuraMessage {
    role: 'user' | 'aura';
    content: string;
    time: string;
    loading?: boolean;
}

const nowTime = () => {
    return moment().format('HH:mm');
};

const messages = ref<AuraMessage[]>([]);

const askAura = (chat: AuraChatMock) => {
    messages.value.push({
        role: 'user',
        content: chat.question,
        time: nowTime(),
    });

    const processingMessageIndex =
        messages.value.push({
            role: 'aura',
            content: 'O Aura está analisando o histórico...',
            time: nowTime(),
            loading: true,
        }) - 1;

    setTimeout(() => {
        messages.value[processingMessageIndex] = {
            role: 'aura',
            content: chat.answer,
            time: nowTime(),
        };
    }, 3000);
};
</script>

<template>
    <UiRightPanel v-model="auraPanelOpened" :title="$t('contact.aura.title')" :icon="SparklesIcon">
        <template #button>
            <UiButton
                size="md"
                variant="white"
                text-variant="gray-700"
                @click="auraPanelOpened = true"
                class="mr-4"
            >
                <img :src="AuraConversationIcon" class="mr-0 h-5 w-5 sm:mr-3" />
                <span class="hidden sm:block">
                    {{ $t('contact.aura.askAura') }}
                </span>
            </UiButton>
        </template>
        <template #default>
            <div class="flex flex-col gap-4">
                <template v-if="messages.length === 0">
                    <!-- Aura initial message -->
                    <div class="flex items-start gap-3">
                        <img :src="AuraConversationIcon" class="h-8 w-8 shrink-0" alt="AURA" />
                        <div
                            class="rounded-2x1 max-w-[85%] rounded-tl-sm bg-white px-4 py-3 text-sm leading-relaxed shadow-sm"
                        >
                            Pergunte ao Aura sobre este contexto. Ele responde com base nas ligações
                            registradas.
                        </div>
                    </div>

                    <!-- Default questions -->
                    <div class="flex flex-col gap-2 pl-11">
                        <button
                            v-for="chat in auraChatMocks"
                            :key="chat.question"
                            type="button"
                            class="hover:border-primary rounded-lg border bg-white px-3 py-2 text-left text-sm transition-colors hover:bg-gray-100"
                            @click="askAura(chat)"
                        >
                            {{ chat.question }}
                        </button>
                    </div>
                </template>

                <!-- Messages -->
                <div
                    v-for="(message, index) in messages"
                    :key="index"
                    class="flex items-start gap-3"
                    :class="{ 'justify-end': message.role === 'user' }"
                >
                    <template v-if="message.role === 'aura'">
                        <div
                            v-if="message.loading"
                            class="bg-primary mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white"
                        >
                            <RefreshIcon
                                class="h-4 w-4 animate-spin"
                                style="animation-duration: 1.5s; animation-direction: reverse"
                            />
                        </div>
                        <img v-else :src="AuraConversationIcon" class="h-8 w-8" alt="AURA" />
                        <div
                            class="max-w-[85%] rounded-bl-xl rounded-br-xl rounded-tr-xl bg-white px-4 py-2.5 text-sm leading-relaxed shadow"
                            :class="message.loading ? 'text-gray-500' : ''"
                        >
                            <p class="whitespace-pre-line">
                                {{ message.content }}
                            </p>
                            <p class="mt-1 text-[11px] text-gray-500">
                                {{ message.time }}
                            </p>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="bg-primary max-w-[80%] rounded-bl-xl rounded-br-xl rounded-tl-xl px-4 py-3 text-sm text-white"
                        >
                            <p class="whitespace-pre-line">
                                {{ message.content }}
                            </p>
                            <p class="mt-1 text-right text-[11px] text-gray-400">
                                {{ message.time }}
                            </p>
                        </div>
                        <UserIcon class="text-primary h-6 w-6 shrink-0" />
                    </template>
                </div>
            </div>
        </template>
    </UiRightPanel>
</template>
